import {
	toSuspensionAffectedBooking,
	toVehicleSuspension,
} from "../utils/mappers/to-vehicle-suspension";
import type {
	LiftVehicleSuspensionInput,
	SuspensionAffectedBooking,
	VehicleSuspension,
	VehicleSuspensionInput,
} from "../types/vehicle-suspension.types";
import {
	changeVehicleSuspension,
	getActiveVehicleSuspension,
	getSuspendedVehicles,
	getSuspensionAffectedBookings,
	getVehicleSuspensionHistory,
	liftVehicleSuspension,
	placeVehicleSuspension,
} from "@/lib/api";

/**
 * Vehicle suspensions (BATT-481). BattSupport or BattAdmin endpoints; the app
 * calls them for a BattAdmin on the admin platform only.
 */
export const vehicleSuspensionService = {
	/** `null` when the vehicle isn't suspended: the BFF answers 204, no body. */
	async getActiveSuspension(
		vehicleId: string,
		signal?: AbortSignal
	): Promise<VehicleSuspension | null> {
		const response = await getActiveVehicleSuspension(vehicleId, signal);
		return response?.id ? toVehicleSuspension(response) : null;
	},

	/** Every active suspension, one per vehicle, longest-running first. */
	async getSuspendedVehicles(
		signal?: AbortSignal
	): Promise<Array<VehicleSuspension>> {
		const response = await getSuspendedVehicles(signal);
		return response.map(toVehicleSuspension);
	},

	/** Newest first, the active one included. */
	async getHistory(
		vehicleId: string,
		signal?: AbortSignal
	): Promise<Array<VehicleSuspension>> {
		const response = await getVehicleSuspensionHistory(vehicleId, signal);
		return response.map(toVehicleSuspension);
	},

	/** Computed live, earliest first; needs an active suspension. */
	async getAffectedBookings(
		vehicleId: string,
		signal?: AbortSignal
	): Promise<Array<SuspensionAffectedBooking>> {
		const response = await getSuspensionAffectedBookings(vehicleId, signal);
		return response.map(toSuspensionAffectedBooking);
	},

	async place(
		vehicleId: string,
		input: VehicleSuspensionInput
	): Promise<VehicleSuspension> {
		const response = await placeVehicleSuspension(vehicleId, input);
		return toVehicleSuspension(response);
	},

	/** Replaces kind, reason and comment: an omitted comment clears it. */
	async change(
		vehicleId: string,
		input: VehicleSuspensionInput
	): Promise<VehicleSuspension> {
		const response = await changeVehicleSuspension(vehicleId, input);
		return toVehicleSuspension(response);
	},

	async lift(
		vehicleId: string,
		input: LiftVehicleSuspensionInput
	): Promise<VehicleSuspension> {
		const response = await liftVehicleSuspension(vehicleId, input);
		return toVehicleSuspension(response);
	},
};
